import { format } from "date-fns";
import { getPlanShortName, isEmptyObj } from "utils";
import { unixToDateTime, FULL_MONTH_YEAR_FORMAT } from "utils/date";

const CARD_BRAND_NAMES = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "American Express",
  diners: "Diners Club",
  discover: "Discover",
  jcb: "JCB",
  unionpay: "UnionPay",
  eftpos_au: "EFTPOS",
};

// One-line summary under the plan name (copy from product: Plus from the Figma
// frame, Free/Core/Pro from the ticket thread).
const PLAN_DESCRIPTIONS = {
  free: "Track your portfolio and explore strategies",
  core: "Invest up to $50k with 1 external brokerage account",
  plus: "Invest up to $150K with no-code strategy builders.",
  pro: "Invest unlimited amount with no-code and full code strategy builders",
};

const capitalize = (text) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

// The backend sends the plan key, e.g. "surmount_core_yearly" -> "Core yearly".
const describeUpcomingPlan = (planKey) => {
  const [tier, interval] = (planKey || "")
    .toLowerCase()
    .replace(/^surmount[-_]/, "")
    .split(/[-_]/);
  if (!tier) return "";
  return `${capitalize(tier)}${interval ? ` ${interval}` : ""}`;
};

const getScheduledMessage = ({ cancelScheduled, upcoming, periodEnd }) => {
  if (cancelScheduled) {
    return `Your downgrade to the Free monthly plan is scheduled for ${formatShortDate(
      periodEnd
    )}.`;
  }
  const when = formatShortDate(upcoming?.start_date || periodEnd);
  const target = describeUpcomingPlan(upcoming?.plan);
  return target
    ? `Your change to the ${target} plan is scheduled for ${when}.`
    : `Your plan change is scheduled for ${when}.`;
};

export const formatCardBrand = (brand) =>
  CARD_BRAND_NAMES[brand] || capitalize((brand || "card").replace(/_/g, " "));

export const formatExpiry = (month, year) =>
  `${String(month).padStart(2, "0")} / ${year}`;

// Stripe sends unix seconds; the design shows "Jul 9, 2026".
export const formatShortDate = (unixSeconds) =>
  unixSeconds ? format(new Date(unixSeconds * 1000), "MMM d, yyyy") : "";

export const getPlanTier = (plan) => {
  const id = (plan?.id || plan?.name || "").toLowerCase();
  if (id.includes("free")) return "free";
  if (id.includes("core")) return "core";
  if (id.includes("plus")) return "plus";
  if (id.includes("pro") || id.includes("premium")) return "pro";
  return "other";
};

const isFreePlan = (plan) =>
  getPlanTier(plan) === "free" || !plan.interval || Number(plan.price) === 0;

// Everything the Billing panel shows, derived from the user-details payload.
export const getBillingState = (user) => {
  const plan = user?.surmount_plan || {};
  const subscription = user?.stripe_subscription || null;
  const hasPlan = !isEmptyObj(plan);
  const isFree = hasPlan ? isFreePlan(plan) : true;
  const card = subscription?.default_payment_method?.card || null;
  const periodEnd = subscription?.current_period_end || null;
  // A scheduled switch to another tier also sets cancel_at_period_end, so only
  // a cancellation with no upcoming plan counts (same rule as the plan modal).
  const cancelScheduled =
    !!subscription?.cancel_at_period_end && !user?.upcoming_plan;

  return {
    hasPlan,
    isFree,
    tier: hasPlan ? getPlanTier(plan) : "free",
    planTitle: hasPlan
      ? `${getPlanShortName(plan.name)} plan`
      : "No active plan",
    planName: plan.name || "",
    interval: !isFree ? capitalize(plan.interval) : "",
    description: hasPlan
      ? PLAN_DESCRIPTIONS[getPlanTier(plan)] || plan.benefits?.[0]?.text || ""
      : "",
    showRenewal: !isFree && !!periodEnd,
    renewalDate: formatShortDate(periodEnd),
    // The existing downgrade/reactivate dialogs show this longer form.
    renewalDateLong: periodEnd
      ? unixToDateTime(periodEnd, FULL_MONTH_YEAR_FORMAT)
      : "",
    cancelScheduled,
    // A switch to another tier is scheduled (the subscription renews as the new plan).
    scheduledChange: !!user?.upcoming_plan,
    renewalMessage:
      cancelScheduled || user?.upcoming_plan
        ? getScheduledMessage({
            cancelScheduled,
            upcoming: user?.upcoming_plan,
            periodEnd,
          })
        : `Your plan renews on ${formatShortDate(periodEnd)}.`,
    modifying: !!user?.modifying,
    card: card
      ? {
          brand: formatCardBrand(card.brand),
          last4: card.last4,
          expiry: formatExpiry(card.exp_month, card.exp_year),
        }
      : null,
    paymentFailed: !!user?.payment_failed,
  };
};
